import React, { useState, useEffect } from 'react';
import TriageFlowLayout from 'components/templates/TriageFlowLayout/TriageFlowLayout';
import { useActivePatient } from 'hooks/useActivePatient';
import './ExamReportPage.css';
import TriageExamReportContent from 'components/organisms/ReportPage/TriageExamReportContent';
import { Tab, TabItem, TabList, TabPanel, TabPanels, Tabs } from 'components/organisms/Tabs';
import Grid from "@mui/material/Grid";
import { Appointment } from 'models/AppointmentModels';
import { FullJoints, Xray } from 'models/XrayModels';
import { PRO } from 'models/SurveyModels';
import HttpService from 'services/httpService';
import { isMissingXrayData } from 'helpers/helpers';
import { ENDPOINT_BASE_URL } from 'hooks/constants';
import { useOrgData } from 'hooks/useOrgData';
import { useAppointmentData } from 'hooks/useAppointmentData';
import { useAppointmentParamsFromQuery } from 'hooks/useAppointmentParamsFromQuery';
import { useProData } from 'hooks/useProData';
import { useXrayData } from 'hooks/useXrayData';
import { useUserData } from 'hooks/useUserData';
import ClassicExamReportContent from 'components/organisms/ReportPage/ClassicExamReportContent';
import { useAuth } from 'hooks/useAuth';

const ExamReportPage: React.FC = () => {

  const { activePatient } = useActivePatient();
  const { accessToken, user } = useAuth();

  const {
    org,
    orgLoading,
  } = useOrgData({ preventRefetch: true });

  const {
    userById,
    userByIdLoading,
  } = useUserData({ preventRefetch: true });

  const {
    appointmentId,
    appointmentDateISOString,
  } = useAppointmentParamsFromQuery();

  const {
    appointment,
    appointmentLoading,
  } = useAppointmentData({
    selectedDateISOString: appointmentDateISOString,
    appointmentId,
    preventRefetch: true,
  });

  const {
    latestProsMappedToJoints,
    allAppointmentProsLoading,
  } = useProData({ appointment, options: { preventRefetch: true } });

  const {
    xraysMappedToJoints,
    allAppointmentXraysLoading,
  } = useXrayData({ appointment, options: { preventRefetch: true } });

  // Use localStorage to persist tab state across re-renders and page reloads
  const getInitialTab = () => {
    try {
      return localStorage.getItem('report-active-tab') || 'provider';
    } catch {
      return 'provider';
    }
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [triagePdfFile, setTriagePdfFile] = useState<Blob | null>(null);
  const [triageLoading, setTriageLoading] = useState<boolean>(false);
  const [triageError, setTriageError] = useState<boolean>(false);
  const [fullPdfFile, setFullPdfFile] = useState<Blob | null>(null);
  const [fullLoading, setFullLoading] = useState<boolean>(false);
  const [fullError, setFullError] = useState<boolean>(false);

  // Track which tabs have had their PDFs generated
  const [generatedPdfs, setGeneratedPdfs] = useState<Set<string>>(new Set());

  // Persist tab state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('report-active-tab', activeTab);
    } catch {
      // Ignore localStorage errors
    }
  }, [activeTab]);


  const initQueriesLoading = appointmentLoading
    || allAppointmentProsLoading
    || allAppointmentXraysLoading
    || orgLoading
    || userByIdLoading;

  type PdfRequestDataType = {
    [FJ in FullJoints]?: {
      pro?: PRO;
      xrays: Xray[];
      priority: number;
    }
  };

  const getJointData = (
    appointment: Appointment,
    prosByJoints: { [FJ in FullJoints]?: PRO },
    xraysByJoints: { [FJ in FullJoints]?: Xray[] },
  ) => {

    try {
      if (!appointment.joints) throw new Error('Missing appointment joints');

      const joints = Object.entries(appointment.joints)
        .filter(([, rank]) => !!rank)
        .sort(([, aRank], [, bRank]) => aRank! - bRank!) as [FullJoints, number][];

      return joints.reduce((obj, [fullJoint, rank]) => {
        const xrays = xraysByJoints[fullJoint];
        if (!xrays) return obj;

        obj[fullJoint] = {
          ...(prosByJoints[fullJoint] ? { pro: prosByJoints[fullJoint] } : undefined),
          xrays: xraysByJoints[fullJoint]!,
          priority: rank,
        };
        if (isMissingXrayData(appointment)) delete obj[fullJoint];
        return obj;
      }, {} as PdfRequestDataType);
    } catch (e) {
      console.error(e);
      setFullError(true);
      setFullLoading(false);
    }
  }

  const generateTriagePdf = async () => {
    try {
      if (!activePatient || !org || !appointment || !appointment.joints || !latestProsMappedToJoints || !xraysMappedToJoints) {
        throw new Error('Patient, PRO, or Xray data missing.');
      }
      setTriageError(false);
      setTriageLoading(true);

      const jointData = getJointData(appointment, latestProsMappedToJoints, xraysMappedToJoints);

      const respData = await HttpService.post<Blob>({
        url: `${ENDPOINT_BASE_URL}/pdf/triage-report?org_id=${user?.orgs[0].org_id}&appointment_date=${appointment.appointment_date}`,
        data: { org, patient: activePatient, joint_data: jointData },
        token: accessToken,
        responseType: 'blob',
      });
      setTriagePdfFile(respData.data);
      setTriageLoading(false);
      setGeneratedPdfs(prev => new Set(prev).add('provider'));
    } catch (e) {
      console.error(e);
      setTriageError(true);
      setTriageLoading(false);
    }
  };

  const generateFullPdf = async () => {
    try {
      if (!activePatient || !org || !appointment || !appointment.joints || !latestProsMappedToJoints || !xraysMappedToJoints) {
        throw new Error('Missing data');
      }
      setFullError(false);
      setFullLoading(true);

      const jointData = getJointData(appointment, latestProsMappedToJoints, xraysMappedToJoints);
      const isoAppt = new Date(appointment.appointment_date).toISOString().replace('Z', '');

      const respData = await HttpService.post<Blob>({
        url: `${ENDPOINT_BASE_URL}/pdf/?org_id=${user?.orgs[0].org_id}`,
        data: { org, patient: activePatient, joint_data: jointData, appointment_date: isoAppt, user_name: userById?.user.name },
        token: accessToken,
        responseType: 'blob',
      });
      setFullPdfFile(respData.data);
      setFullLoading(false);
      setGeneratedPdfs(prev => new Set(prev).add('patient'));
    } catch (e) {
      console.error(e);
      setFullError(true);
      setFullLoading(false);
    }
  };


  // Generate PDF for the currently active tab when data is ready
  useEffect(() => {
    if (
      initQueriesLoading ||
      !appointment ||
      !appointment.joints ||
      !xraysMappedToJoints ||
      !latestProsMappedToJoints
    ) return;


    // Only generate PDF for the currently active tab
    if (activeTab === 'provider' && !generatedPdfs.has('provider')) {
      generateTriagePdf();
    } else if (activeTab === 'patient' && !generatedPdfs.has('patient')) {
      generateFullPdf();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initQueriesLoading, org, appointment, xraysMappedToJoints, latestProsMappedToJoints, activeTab, generatedPdfs]);


  const tabData: TabItem[] = [
    {
      id: 'provider',
      label: 'Provider',
      content: <TriageExamReportContent
        pdfFile={triagePdfFile}
        pdfLoading={triageLoading}
        pdfError={triageError}
        isVisible={activeTab === 'provider'}
        appointment={appointment}
        activePatient={activePatient}
        queriesLoading={initQueriesLoading && !triagePdfFile}
        generatePdf={generateTriagePdf}
      />,
    },
    {
      id: 'patient',
      label: 'Patient',
      content: <ClassicExamReportContent
        pdfFile={fullPdfFile}
        pdfLoading={fullLoading}
        pdfError={fullError}
        isVisible={activeTab === 'patient'}
        appointment={appointment}
        activePatient={activePatient}
        queriesLoading={initQueriesLoading && !fullPdfFile}
        generatePdf={generateFullPdf}
      />,
    },
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  return (
    <TriageFlowLayout singlePatient={activePatient} title="Summary Report">
      <Grid container spacing={3} paddingTop={3}>
        <Grid item xs={12}>
          <Tabs
            tabs={tabData}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            orientation="horizontal"
          >
            <TabList>
              {tabData.map((tab) => (
                <Tab key={tab.id} id={tab.id} disabled={tab.disabled}>
                  {tab.label}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {tabData.map((tab) => (
                <TabPanel key={tab.id} id={tab.id}>
                  {tab.content}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Grid>
      </Grid>
    </TriageFlowLayout>
  );
};

export default ExamReportPage;
